import { Fragment, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  parseVocabList,
  parseQuoteInterlude,
  buildMarkdownBlocks,
  type VocabListItem,
  type QuoteInterlude,
} from '@org/utils-client';
import { ContentType } from '../../../types/contents';
import type {
  StudentBlock,
  StudentContent,
  StudentExerciseContent,
  StudentTextContent,
} from '../hooks/use-class-blocks';
import {
  deriveBlockKind,
  deriveBlockMinutes,
} from '../hooks/zine-derive';
import type { LatestSubmissionResult, ExerciseSubmissionRecord } from '../types/exercise';
import type { RenderContentOptions } from '../hooks/content-renderer-types';
import {
  Spread,
  SpreadHead,
  Cols,
  Prose,
  Lead,
  VocabClip,
  AudioStrip,
  InterludeQuote,
} from '../zine';
import {
  renderInlineMarkdown,
  renderMarkdownBlocks,
} from '../../custom/markdown';
import { ExerciseSubmissionHistory } from './shared/exercise-submission-history';
import { useLessonLinkClasses } from './shared/lesson-link-classes';

interface ClassBlockListProps {
  /** blocks pre-sorted by order */
  blocks: StudentBlock[];
  latestSubmissionsByExerciseId: Record<string, LatestSubmissionResult>;
  submissionHistoriesByExerciseId?: Record<string, ExerciseSubmissionRecord[]>;
  renderContent: (content: StudentContent, options?: RenderContentOptions) => ReactNode;
  mode?: 'student' | 'teacher';
  /** Exercise showing the deep-link arrival ring (see ClassDetailPage). */
  highlightedExerciseId?: string | null;
  /** Sections the teacher marked as homework for the reader's class session(s). */
  homeworkBlockIds?: string[];
}

/**
 * The issue body: every block renders as a numbered magazine spread
 * (#s1…#sN anchors), with vocab-notation text contents as clipped cards in
 * the aside column, quote-notation text contents as full-bleed interludes
 * between spreads, audio as the magazine audio strip, and exercises
 * full-width below the spread columns.
 */
export function ClassBlockList({
  blocks,
  latestSubmissionsByExerciseId,
  submissionHistoriesByExerciseId,
  renderContent,
  mode = 'student',
  highlightedExerciseId = null,
  homeworkBlockIds = [],
}: ClassBlockListProps) {
  const homeworkSet = useMemo(() => new Set(homeworkBlockIds), [homeworkBlockIds]);
  const { t } = useTranslation('p2e');

  if (blocks.length === 0) {
    return (
      <div className="z-page z-state-page">
        <p>{t('class.emptyContent')}</p>
      </div>
    );
  }

  return (
    <>
      {blocks.map((block, index) => (
        <BlockSpread
          isHomework={homeworkSet.has(block.id)}
          key={block.id}
          block={block}
          index={index}
          latestSubmissionsByExerciseId={latestSubmissionsByExerciseId}
          submissionHistoriesByExerciseId={submissionHistoriesByExerciseId}
          renderContent={renderContent}
          mode={mode}
          highlightedExerciseId={highlightedExerciseId}
        />
      ))}
    </>
  );
}

// ── Block spread ─────────────────────────────────────────────────────────────

type FlowContent = Exclude<StudentContent, StudentExerciseContent>;

interface PartitionedBlockContents {
  /** contents rendered in the prose flow (text/image/video/audio/document) */
  flow: FlowContent[];
  /** vocab clippings collected for the aside column */
  vocabItems: VocabListItem[];
  /** full-bleed quote interludes rendered after the spread */
  interludes: QuoteInterlude[];
  /** exercises rendered full-width below the columns */
  exercises: StudentExerciseContent[];
  /** id of the first flow text content (gets the Lead treatment) */
  firstTextContentId: string | null;
}

function partitionBlockContents(contents: StudentContent[]): PartitionedBlockContents {
  const flow: FlowContent[] = [];
  const vocabItems: VocabListItem[] = [];
  const interludes: QuoteInterlude[] = [];
  const exercises: StudentExerciseContent[] = [];
  let firstTextContentId: string | null = null;

  contents.forEach((content) => {
    if (content.type === ContentType.Exercise) {
      exercises.push(content);
      return;
    }

    if (content.type === ContentType.Text) {
      const vocab = parseVocabList(content.text);
      if (vocab) {
        vocabItems.push(...vocab);
        return;
      }

      const quote = parseQuoteInterlude(content.text);
      if (quote) {
        interludes.push(quote);
        return;
      }

      if (firstTextContentId === null) {
        firstTextContentId = content.id;
      }
    }

    flow.push(content);
  });

  return { flow, vocabItems, interludes, exercises, firstTextContentId };
}

interface BlockSpreadProps {
  block: StudentBlock;
  index: number;
  latestSubmissionsByExerciseId: Record<string, LatestSubmissionResult>;
  submissionHistoriesByExerciseId?: Record<string, ExerciseSubmissionRecord[]>;
  renderContent: (content: StudentContent, options?: RenderContentOptions) => ReactNode;
  mode: 'student' | 'teacher';
  highlightedExerciseId: string | null;
  isHomework?: boolean;
}

function BlockSpread({
  block,
  index,
  latestSubmissionsByExerciseId,
  submissionHistoriesByExerciseId,
  renderContent,
  mode,
  highlightedExerciseId,
  isHomework = false,
}: BlockSpreadProps) {
  const { t } = useTranslation('p2e');
  const isTeacherView = mode === 'teacher';

  const { flow, vocabItems, interludes, exercises, firstTextContentId } = useMemo(
    () => partitionBlockContents(block.contents),
    [block.contents],
  );

  const kind = deriveBlockKind(block.contents);
  const kindLabel = t(`zine.kind.${kind}`);
  const minutes = deriveBlockMinutes(block.contents);

  const proseColumn = (
    <Prose>
      {flow.map((content) => (
        <FlowContentDisplay
          key={content.id}
          content={content}
          isLeadText={content.id === firstTextContentId}
          renderContent={renderContent}
        />
      ))}
    </Prose>
  );

  return (
    <Fragment>
      <Spread id={`s${index + 1}`}>
        <SpreadHead
          no={index + 1}
          eyebrow={
            <>
              {isHomework && (
                <span className="z-homework-tag" data-testid="homework-tag">
                  {t('zine.homework')}
                </span>
              )}
              {`${kindLabel} · ${t('zine.minutes', { n: minutes })}`}
            </>
          }
          title={block.name?.trim() || t('zine.untitledBlock', { n: index + 1 })}
        />
        {vocabItems.length > 0 ? (
          <Cols>
            {proseColumn}
            <aside aria-label={t('zine.vocabAria')}>
              {vocabItems.map((item, itemIndex) => (
                <VocabClip
                  key={`${block.id}-vocab-${itemIndex}`}
                  term={item.term}
                  category={item.category}
                  definition={item.definition}
                  alt={itemIndex % 2 === 1}
                />
              ))}
            </aside>
          </Cols>
        ) : (
          proseColumn
        )}
        {exercises.map((content) => (
          <ExerciseSlot
            key={content.id}
            content={content}
            latestSubmission={
              content.exerciseId
                ? latestSubmissionsByExerciseId[content.exerciseId]
                : undefined
            }
            history={
              isTeacherView && content.exerciseId
                ? submissionHistoriesByExerciseId?.[content.exerciseId] ?? []
                : undefined
            }
            renderContent={renderContent}
            isTeacherView={isTeacherView}
            isHighlighted={Boolean(content.exerciseId) && content.exerciseId === highlightedExerciseId}
          />
        ))}
      </Spread>
      {interludes.map((interlude, interludeIndex) => (
        <InterludeQuote
          key={`${block.id}-interlude-${interludeIndex}`}
          quote={renderInlineMarkdown(
            interlude.quote,
            `${block.id}-interlude-${interludeIndex}-quote`,
          )}
          attribution={
            <>
              —{' '}
              {renderInlineMarkdown(
                interlude.attribution,
                `${block.id}-interlude-${interludeIndex}-who`,
              )}
            </>
          }
          ariaLabel={t('zine.quoteAria')}
        />
      ))}
    </Fragment>
  );
}

// ── Prose flow contents ──────────────────────────────────────────────────────

function FlowContentDisplay({
  content,
  isLeadText,
  renderContent,
}: {
  content: FlowContent;
  isLeadText: boolean;
  renderContent: (content: StudentContent, options?: RenderContentOptions) => ReactNode;
}) {
  const { t } = useTranslation('p2e');

  if (content.type === ContentType.Text) {
    return <ZineTextContent content={content} lead={isLeadText} />;
  }

  if (content.type === ContentType.Audio) {
    const captionParts = [content.title, content.description]
      .map((part) => part?.trim())
      .filter((part): part is string => Boolean(part));
    return (
      <AudioStrip
        src={content.url}
        caption={
          captionParts.length > 0
            ? renderInlineMarkdown(captionParts.join(' · '), `${content.id}-caption`)
            : undefined
        }
        playLabel={t('zine.audio.play')}
        pauseLabel={t('zine.audio.pause')}
        seekLabel={t('zine.audio.seek')}
      />
    );
  }

  // Images, videos and documents keep their existing display components.
  return (
    <div className="z-media">
      {renderContent(content, { variant: 'inline' })}
    </div>
  );
}

/**
 * Prose rendering for a text content: markdown body with the first
 * paragraph of the block's first text content styled as the Lead.
 * Title/description render as a task heading and a bold instruction line
 * so authored metadata is not lost.
 */
function ZineTextContent({ content, lead }: { content: StudentTextContent; lead: boolean }) {
  const linkClasses = useLessonLinkClasses();
  const markdownBlocks = useMemo(
    () => buildMarkdownBlocks(content.text ?? ''),
    [content.text],
  );

  // A titled content is a task: its first paragraph is a step, not an intro.
  const leadBlock =
    lead && !content.title?.trim() && markdownBlocks.length > 0 && markdownBlocks[0].type === 'paragraph'
      ? markdownBlocks[0]
      : null;
  const bodyBlocks = leadBlock ? markdownBlocks.slice(1) : markdownBlocks;

  return (
    <>
      {content.title?.trim() && (
        <h3>{renderInlineMarkdown(content.title, `${content.id}-title`)}</h3>
      )}
      {content.description?.trim() && (
        <p className="z-task-instruction">
          {renderInlineMarkdown(content.description, `${content.id}-desc`, linkClasses)}
        </p>
      )}
      {leadBlock && (
        <Lead>{renderInlineMarkdown(leadBlock.content, `${content.id}-lead`, linkClasses)}</Lead>
      )}
      {renderMarkdownBlocks(bodyBlocks, `${content.id}-body`, linkClasses)}
    </>
  );
}

// ── Exercises ────────────────────────────────────────────────────────────────

function ExerciseSlot({
  content,
  latestSubmission,
  history,
  renderContent,
  isTeacherView,
  isHighlighted = false,
}: {
  content: StudentExerciseContent;
  latestSubmission?: LatestSubmissionResult;
  history?: ExerciseSubmissionRecord[];
  renderContent: (content: StudentContent, options?: RenderContentOptions) => ReactNode;
  isTeacherView: boolean;
  isHighlighted?: boolean;
}) {
  const contentWithSubmission: StudentContent = {
    ...content,
    lastSubmission: latestSubmission?.submission ?? null,
    lastEvaluation: latestSubmission?.evaluation ?? null,
  } as StudentContent;

  return (
    // The id lets a notification deep-link scroll straight to this exercise.
    <div
      className={isHighlighted ? 'z-exercise z-exercise--target' : 'z-exercise'}
      id={content.exerciseId ? `exercise-${content.exerciseId}` : undefined}
    >
      {latestSubmission && (
        <p className="z-attempt">
          Last attempt #{latestSubmission.attemptNumber} · {new Date(latestSubmission.submittedAt).toLocaleString()}
        </p>
      )}
      {renderContent(contentWithSubmission)}
      {isTeacherView && history && history.length > 0 && (
        <ExerciseSubmissionHistory
          content={contentWithSubmission as StudentExerciseContent}
          submissions={history}
          isTeacherView={isTeacherView}
        />
      )}
    </div>
  );
}
